// src/components/AdminPanel.jsx
// Admin command center — tabbed: Tasks · Alerts · Users · Vendors · Access · Settings.

import React, { useState as useState_AD } from "react";
import * as PPIcons from "./_icons";
import { Avatar, Button, CategoryChip, Chip, Empty, Field, SectionHead, Segmented, SeverityChip, StatusChip, Tabs, formatRelative } from "./_primitives";
import PinchIcon from "./PinchIcon";
import TaskPanel from "./TaskPanel";
import SystemicAlertsPanel from "./SystemicAlertsPanel";

const ADMIN_TABS = [
  { id: "tasks",   label: "Tasks",   icon: <PPIcons.IconClipboard /> },
  { id: "alerts",  label: "Alerts",  icon: <PPIcons.IconAlert /> },
  { id: "users",   label: "Users",   icon: <PPIcons.IconUsers /> },
  { id: "vendors", label: "Vendors", icon: <PPIcons.IconBuilding /> },
  { id: "access",  label: "Access",  icon: <PPIcons.IconLink /> },
  { id: "settings", label: "Settings", icon: <PPIcons.IconCog /> },
];

function AdminPanel({
  orgId, orgName, isSuperUser,
  allPosts = [], resolvedPosts = [], tasks = [],
  alerts = [], members = [], vendors = [], joinRequests = [],
  createTask, assignTask, deleteTask, createDraftTasks, approveDraftTask,
  guidelines = [], guidelineItems = [], categories = [],
  updateGuidelines, updateCategories,
  branding, updateBranding,
  onSendInvite,
}) {
  const [tab, setTab] = useState_AD("tasks");

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 24px 60px", width: "100%" }}>
      <header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 18, flexWrap: "wrap", gap: 10,
      }}>
        <div>
          <h1 style={{
            fontSize: 22, fontWeight: 700, color: "var(--text-strong)",
            letterSpacing: "-0.01em",
          }}>Admin</h1>
          <div style={{ fontSize: 12, color: "var(--text-faint)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
            {orgName} · {members.length} members · {tasks.filter(t => t.status === "open").length} open tasks
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Chip tone={isSuperUser ? "ai" : "category"} dot={false}>
            {isSuperUser ? "Super user" : "Admin"}
          </Chip>
        </div>
      </header>

      <div style={{ marginBottom: 16 }}>
        <Tabs value={tab} onChange={setTab} tabs={ADMIN_TABS} />
      </div>

      {tab === "tasks" && (
        <TaskPanel
          tasks={tasks} members={members} allPosts={[...allPosts, ...resolvedPosts]}
          onCreateTask={createTask}
          onApproveDraft={approveDraftTask}
        />
      )}

      {tab === "alerts" && (
        <SystemicAlertsPanel alerts={alerts} />
      )}

      {tab === "users" && (
        <UsersTab members={members} joinRequests={joinRequests} />
      )}

      {tab === "vendors" && (
        <VendorsTab vendors={vendors} />
      )}

      {tab === "access" && (
        <AccessTab orgId={orgId} orgName={orgName} onSendInvite={onSendInvite} />
      )}

      {tab === "settings" && (
        <SettingsTab
          orgName={orgName}
          categories={categories}
          guidelines={guidelines}
          guidelineItems={guidelineItems}
          updateCategories={updateCategories}
          updateGuidelines={updateGuidelines}
          branding={branding}
          updateBranding={updateBranding}
        />
      )}
    </div>
  );
}

/* ============================================================
   USERS TAB
   ============================================================ */
function UsersTab({ members = [], joinRequests = [] }) {
  const [search, setSearch] = useState_AD("");
  const filtered = members.filter(m =>
    !search || m.displayName?.toLowerCase().includes(search.toLowerCase()) ||
              m.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {joinRequests.length > 0 && (
        <div className="pp-surface" style={{
          background: "var(--accent-soft)", borderColor: "var(--accent-soft-bd)",
        }}>
          <SectionHead
            title="Pending join requests"
            sub={`${joinRequests.length} waiting for approval`}
          />
          <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            {joinRequests.map(r => (
              <div key={r.uid} style={{
                background: "var(--surface)", padding: "10px 14px",
                borderRadius: "var(--radius-sm)",
                display: "flex", alignItems: "center", gap: 12,
              }}>
                <Avatar name={r.displayName} size="sm" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.displayName}</div>
                  <div style={{ fontSize: 11, color: "var(--text-faint)" }}>{r.email}</div>
                </div>
                <Button variant="primary" size="sm">Approve</Button>
                <Button variant="ghost" size="sm">Reject</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pp-surface">
        <SectionHead
          title="Members"
          sub={`${members.length} active`}
          right={
            <div style={{ display: "flex", gap: 6 }}>
              <input className="pp-input" placeholder="Search…"
                value={search} onChange={e => setSearch(e.target.value)}
                style={{ height: 32, width: 180, fontSize: 13 }} />
              <Button variant="primary" size="sm" icon={<PPIcons.IconPlus />}>Invite</Button>
            </div>
          }
        />
        <div style={{ overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--divider)" }}>
                {["Member", "Role", "Status", "Joined", ""].map((h, i) => (
                  <th key={i} style={{
                    textAlign: "left", padding: "10px 14px",
                    fontSize: 11, fontWeight: 700, color: "var(--text-faint)",
                    textTransform: "uppercase", letterSpacing: 0.06,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => <UserRow key={m.uid} member={m} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UserRow({ member }) {
  return (
    <tr style={{ borderBottom: "1px solid var(--divider)" }}>
      <td style={{ padding: "12px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar name={member.displayName} size="sm" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{member.displayName}</div>
            <div style={{ fontSize: 11, color: "var(--text-faint)" }}>{member.email}</div>
          </div>
        </div>
      </td>
      <td style={{ padding: "12px 14px" }}>
        <Chip tone={member.role === "superuser" ? "ai" : "category"} dot={false}>
          {member.role === "superuser" ? "Super user" : member.role === "admin" ? "Admin" : "Member"}
        </Chip>
      </td>
      <td style={{ padding: "12px 14px" }}>
        {member.disabled
          ? <StatusChip status="dismissed" />
          : <StatusChip status="resolved" />}
      </td>
      <td style={{ padding: "12px 14px", fontSize: 12, color: "var(--text-faint)", fontFamily: "var(--font-mono)" }}>
        {formatRelative(member.createdAt) || "—"}
      </td>
      <td style={{ padding: "12px 14px", textAlign: "right" }}>
        <Button variant="ghost" size="sm" icon={<PPIcons.IconMore />} />
      </td>
    </tr>
  );
}

/* ============================================================
   VENDORS TAB
   ============================================================ */
function VendorsTab({ vendors = [] }) {
  return (
    <div className="pp-surface">
      <SectionHead
        title="Vendors"
        sub="AI uses these to suggest tasks with the right contact"
        right={<Button variant="primary" size="sm" icon={<PPIcons.IconPlus />}>Add vendor</Button>}
      />
      <div style={{ padding: 14 }}>
        {vendors.length === 0 ? (
          <Empty
            icon={<PPIcons.IconBuilding />}
            title="No vendors yet"
            sub="Add vendors so AI can match issues to the right contact."
            action={<Button variant="primary" size="sm">Add first vendor</Button>}
          />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
            {vendors.map(v => <VendorCard key={v.id} vendor={v} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function VendorCard({ vendor }) {
  return (
    <div style={{
      padding: 14, border: "1px solid var(--border)",
      borderRadius: "var(--radius-sm)", background: "var(--surface)",
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: "var(--surface-2)",
          display: "grid", placeItems: "center", color: "var(--text-dim)",
        }}>
          <PPIcons.IconBuilding />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{vendor.name}</div>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>{vendor.scope}</div>
        </div>
        <Button variant="ghost" size="sm" icon={<PPIcons.IconMore />} />
      </div>
      {(vendor.phone || vendor.email) && (
        <div style={{
          display: "flex", flexDirection: "column", gap: 2,
          fontSize: 11, color: "var(--text-faint)", fontFamily: "var(--font-mono)",
          paddingTop: 6, borderTop: "1px solid var(--divider)",
        }}>
          {vendor.phone && <span>📞 {vendor.phone}</span>}
          {vendor.email && <span>✉ {vendor.email}</span>}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ACCESS TAB — invites + public submission link
   ============================================================ */
function AccessTab({ orgId, orgName, onSendInvite }) {
  const [email, setEmail] = useState_AD("");
  const publicLink = `${window.location.origin}/submit?org=${orgId}`;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div className="pp-surface">
        <SectionHead
          title="Invite members"
          sub="Send a one-time signup link via email"
        />
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <Field label="Email address">
            <input
              className="pp-input" type="email" placeholder="colleague@company.com"
              value={email} onChange={e => setEmail(e.target.value)}
            />
          </Field>
          <Button variant="primary" icon={<PPIcons.IconSend />}
            onClick={() => onSendInvite && onSendInvite(email)}>
            Send invite
          </Button>
          <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.4 }}>
            Invitee will create an account and join <strong>{orgName}</strong>. Token expires after first use.
          </div>
        </div>
      </div>

      <div className="pp-surface">
        <SectionHead
          title="Public submission link"
          sub="Anonymous · no account required"
        />
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Direct link">
            <div style={{ display: "flex", gap: 6 }}>
              <input className="pp-input" readOnly value={publicLink} style={{ fontFamily: "var(--font-mono)", fontSize: 12 }} />
              <Button variant="secondary" icon={<PPIcons.IconCopy />}>Copy</Button>
            </div>
          </Field>
          <div style={{
            background: "var(--surface-2)", borderRadius: "var(--radius-sm)",
            padding: 16, display: "flex", alignItems: "center", gap: 14,
          }}>
            <div style={{
              width: 100, height: 100, background: "var(--bg-elevated)",
              border: "1px solid var(--border)", borderRadius: 8,
              display: "grid", placeItems: "center", color: "var(--text-dim)",
            }}>
              <PPIcons.IconQr style={{ width: 56, height: 56 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>QR code</div>
              <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 4, lineHeight: 1.4 }}>
                Print or display anywhere. Workers scan to submit anonymously.
              </div>
              <Button variant="secondary" size="sm" style={{ marginTop: 8 }} icon={<PPIcons.IconExternal />}>
                Download PNG
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SETTINGS TAB — categories, guidelines, branding
   ============================================================ */
function SettingsTab({ orgName, categories = [], guidelines = [], guidelineItems = [], branding,
                      updateCategories, updateGuidelines, updateBranding }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="pp-surface">
        <SectionHead
          title="Categories"
          sub="Pinch points are tagged with one of these"
          right={<Button variant="ghost" size="sm" icon={<PPIcons.IconEdit />}>Edit</Button>}
        />
        <div style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {categories.map(c => <CategoryChip key={c} category={c} />)}
        </div>
      </div>

      <div className="pp-surface">
        <SectionHead
          title="Submission guidelines"
          sub={`${guidelineItems.length} rules shown to submitters`}
          right={<Button variant="ghost" size="sm" icon={<PPIcons.IconEdit />}>Edit</Button>}
        />
        <ol style={{
          padding: "14px 14px 14px 36px", margin: 0,
          display: "flex", flexDirection: "column", gap: 6,
          fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5,
        }}>
          {guidelineItems.map((g, i) => <li key={i}>{g}</li>)}
        </ol>
      </div>

      <div className="pp-surface">
        <SectionHead
          title="Branding"
          sub="Product name + logo shown in the header"
          right={<Button variant="ghost" size="sm" icon={<PPIcons.IconEdit />}>Customize</Button>}
        />
        <div style={{ padding: 16, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 10, background: "var(--accent)",
            color: "var(--accent-fg)", display: "grid", placeItems: "center",
          }}>
            <PinchIcon size={28} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{branding?.productName || "Pinch Point"}</div>
            <div style={{ fontSize: 12, color: "var(--text-dim)" }}>Default branding</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
